import { Plus, Trash2 } from 'lucide-react'
import type { BoekingGegevens, BoekRegel, ExactVelden } from '../../../gedeeld/types'
import { Knop } from '../ui'
import { DatumVeld, GetalVeld, KeuzeVeld, naamVan, TekstVeld, Totalen, useSleutels, type NaamSoort } from './ExactDelen'

/* ===========================================================================
 *  Een boeking op de Exact-kaart: inkoop, verkoop, memoriaal, bank of kas
 *
 *  Bovenaan de kop (bv, dagboek, datum, relatie), daaronder de regels als
 *  kaartjes: op een telefoon is een tabel van zeven kolommen niet te
 *  bedienen, en een kaartje per regel is dat op elke breedte wel. Onderaan
 *  de totalen zoals de SERVER ze uitrekende -- niet hier, want dan zou het
 *  scherm iets anders kunnen zeggen dan wat er naar Exact gaat.
 * =========================================================================== */

const LEGE_REGEL: BoekRegel = { omschrijving: '', rekening: '', bedrag: 0 }

export function ExactBoeking({ v, g, onV, gecontroleerd, oud, meldOngeldig }: {
  v: ExactVelden
  g: BoekingGegevens
  onV: (v: ExactVelden) => void
  /** De laatste controle hoort bij wat er nu staat: "niet gevonden" mag dan getoond worden. */
  gecontroleerd: boolean
  /** De totalen horen bij een oudere stand. */
  oud: boolean
  meldOngeldig: (sleutel: string) => (ja: boolean) => void
}) {
  const zet = (n: Partial<BoekingGegevens>) => onV({ ...v, gegevens: { ...g, ...n } })
  const zetRegel = (i: number, n: Partial<BoekRegel>) => zet({ regels: g.regels.map((r, j) => (j === i ? { ...r, ...n } : r)) })
  const { ids, erbij, weg } = useSleutels(g.regels.length)
  const factuur = g.soort === 'inkoop' || g.soort === 'verkoop'
  const metRelatiePerRegel = !factuur
  const bankKas = g.soort === 'bank' || g.soort === 'kas'
  const metDatumPerRegel = bankKas
  const metKostendrager = !!v.keuzes?.kostendragers?.length || g.regels.some((r) => r.kostendrager)
  const naam = (soort: NaamSoort, code: string | undefined) => naamVan(v.berekend, soort, code)

  return (
    <>
      <label className="veld">
        <span className="veld-label">Bv</span>
        <select className="invoer" value={v.divisies[0] ?? ''} onChange={(e) => onV({ ...v, divisies: e.target.value ? [e.target.value] : [] })}
          aria-invalid={!v.divisies.length || undefined}>
          {!v.divisies.length && <option value="">Kies een bv…</option>}
          {v.mogelijk.map((d) => <option key={d.code} value={d.code}>{d.naam}</option>)}
        </select>
      </label>
      <KeuzeVeld label="Dagboek" waarde={g.dagboek} onWaarde={(dagboek) => zet({ dagboek })} keuzes={v.keuzes?.dagboeken}
        leeg="Het standaard dagboek" onder={!v.keuzes?.dagboeken?.length && naam('dagboek', g.dagboek) ? <span className="ex-naam">{naam('dagboek', g.dagboek)}</span> : null} />
      <DatumVeld label="Boekdatum" waarde={g.datum} onWaarde={(datum) => zet({ datum })} />
      {factuur && (
        <TekstVeld label={g.soort === 'inkoop' ? 'Leverancier (code)' : 'Klant (code)'} mono waarde={g.relatie}
          onWaarde={(relatie) => zet({ relatie })} naam={naam('relatie', g.relatie)} nietGevonden={gecontroleerd} />
      )}
      <TekstVeld label="Omschrijving" breed waarde={g.omschrijving} onWaarde={(omschrijving) => zet({ omschrijving })} />
      {factuur && (
        <TekstVeld label={g.soort === 'inkoop' ? 'Factuurnummer van de leverancier' : 'Ons factuurnummer of kenmerk'} mono
          waarde={g.referentie} onWaarde={(referentie) => zet({ referentie })} />
      )}
      {g.soort === 'inkoop' && <DatumVeld label="Vervaldatum" waarde={g.vervaldatum} onWaarde={(vervaldatum) => zet({ vervaldatum })} />}
      <TekstVeld label="Valuta" mono waarde={g.valuta} onWaarde={(valuta) => zet({ valuta: valuta.toUpperCase() })} placeholder="EUR" />
      {factuur && (
        <GetalVeld label="Totaal incl. btw op de factuur" waarde={g.totaal_incl} leegMag placeholder="ter controle"
          onWaarde={(totaal_incl) => zet({ totaal_incl })} onOngeldig={meldOngeldig('totaal')} />
      )}
      <div className="ex-twee">
        <GetalVeld label="Boekjaar" waarde={g.jaar} decimalen={0} leegMag placeholder="uit datum"
          onWaarde={(jaar) => zet({ jaar })} onOngeldig={meldOngeldig('jaar')} />
        <GetalVeld label="Periode" waarde={g.periode} decimalen={0} leegMag placeholder="uit datum"
          onWaarde={(periode) => zet({ periode })} onOngeldig={meldOngeldig('periode')} />
      </div>

      <div className="veld breed">
        <span className="veld-label">Regels ({g.regels.length})</span>
        <ol className="ex-regels">
          {g.regels.map((r, i) => (
            <li key={ids[i]} className="ex-regel">
              <div className="ex-regel-kop">
                <span className="ex-regel-nr">Regel {i + 1}</span>
                <Knop klein soort="stil" icoon={<Trash2 size={13} />} aria-label={`Regel ${i + 1} weghalen`} title="Regel weghalen"
                  disabled={g.regels.length <= 1} onClick={() => { weg(i); zet({ regels: g.regels.filter((_, j) => j !== i) }) }} />
              </div>
              <div className="ex-regel-velden">
                <TekstVeld label="Omschrijving" breed waarde={r.omschrijving} onWaarde={(omschrijving) => zetRegel(i, { omschrijving })} />
                <TekstVeld label="Rekening" mono waarde={r.rekening} onWaarde={(rekening) => zetRegel(i, { rekening: rekening.trim() })}
                  naam={naam('rekening', r.rekening)} nietGevonden={gecontroleerd} />
                <GetalVeld label={factuur ? 'Bedrag excl. btw' : bankKas ? 'Bedrag (+ erbij, − eraf)' : 'Bedrag (+ debet, − credit)'} waarde={r.bedrag}
                  onWaarde={(bedrag) => zetRegel(i, { bedrag: bedrag ?? 0 })} onOngeldig={meldOngeldig(`r${ids[i]}`)} />
                {(factuur || g.soort === 'memoriaal' || r.btw) && (
                  <KeuzeVeld label="Btw" waarde={r.btw} onWaarde={(btw) => zetRegel(i, { btw })} keuzes={v.keuzes?.btw} leeg="Geen btw" />
                )}
                <KeuzeVeld label="Kostenplaats" waarde={r.kostenplaats} onWaarde={(kostenplaats) => zetRegel(i, { kostenplaats })}
                  keuzes={v.keuzes?.kostenplaatsen} leeg="Geen" />
                {metKostendrager && (
                  <KeuzeVeld label="Kostendrager" waarde={r.kostendrager} onWaarde={(kostendrager) => zetRegel(i, { kostendrager })}
                    keuzes={v.keuzes?.kostendragers} leeg="Geen" />
                )}
                {metRelatiePerRegel && (
                  <TekstVeld label="Relatie (code)" mono waarde={r.relatie} onWaarde={(relatie) => zetRegel(i, { relatie: relatie.trim() })}
                    placeholder="optioneel" naam={naam('relatie', r.relatie)} nietGevonden={gecontroleerd} />
                )}
                {metDatumPerRegel && (
                  <DatumVeld label="Datum (leeg = boekdatum)" waarde={r.datum} onWaarde={(datum) => zetRegel(i, { datum })} />
                )}
              </div>
            </li>
          ))}
        </ol>
        <div>
          <Knop klein icoon={<Plus size={13} />} onClick={() => { erbij(); zet({ regels: [...g.regels, { ...LEGE_REGEL }] }) }}>Regel toevoegen</Knop>
        </div>
      </div>

      {/* Bank en kas lees je vanuit de bank of kas zelf, zoals op het
          afschrift; memoriaal in debet en credit. Afspraak met de server
          (soorten.ts, BoekRegel.bedrag). */}
      <Totalen totalen={v.berekend?.totalen} valuta={g.valuta} oud={oud} uitleg={factuur ? null : bankKas ? (
        <>Bedragen gelezen vanuit de {g.soort === 'bank' ? 'bank' : 'kas'}, zoals op het afschrift: <strong>+ is geld erbij</strong> (ontvangst), <strong>− is geld eraf</strong> (betaling).</>
      ) : (
        <>Bedragen: <strong>+ is debet</strong>, <strong>− is credit</strong>. Debet en credit moeten samen gelijk zijn.</>
      )} />
    </>
  )
}
